import { useAuthStore } from '../../auth/store/useAuthStore';
import { syncCatalogSnapshotFromRemote } from '../../catalog/infra/catalogSnapshotSync';
import { getConnectivityStatus, subscribeConnectivity, isOnline } from '../../shell/connectivity/connectivityInfra';
import {
  markMastersSyncedOk,
  shouldAutoPullCatalog,
} from './masterSyncPolicy';
import { pullCustomerDataWhenNeeded } from './pullCustomerWhenNeeded';

const REMOTE_REFRESH_COOLDOWN_MS = 15_000;

let lastRemoteRefreshAt = 0;
let refreshInFlight: Promise<void> | null = null;

export type RefreshRemoteDomainsOpts = {
  /** Ignora cooldown corto anti-ráfaga (p. ej. pull manual forzado). */
  force?: boolean;
  /** Si true, siempre intenta catálogo aunque cooldown maestros activo. */
  forceCatalog?: boolean;
  /** Si true, incluye pull customer (tab, checkout, manual). Por defecto false en timer/realtime. */
  includeCustomer?: boolean;
};

async function runRefreshRemoteDomains(opts?: RefreshRemoteDomainsOpts): Promise<void> {
  const now = Date.now();
  if (
    !opts?.force &&
    lastRemoteRefreshAt > 0 &&
    now - lastRemoteRefreshAt < REMOTE_REFRESH_COOLDOWN_MS
  ) {
    return;
  }

  const { session } = useAuthStore.getState();
  if (!session) return;

  const online = await isOnline();
  if (!online) return;

  const pullCatalog = await shouldAutoPullCatalog({
    force: opts?.force,
    forceCatalog: opts?.forceCatalog,
    now,
  });

  if (pullCatalog) {
    // Serialización en syncCatalogSnapshotFromRemote (singleflight + withSyncMutex).
    // No usar withSyncMutex aquí: anidar mutexes bloquea el login (session-ready + POS preflight).
    // forceCatalog = bypass cooldown; NUNCA forceFull aquí (timer/focus/reconnect = incremental).
    await syncCatalogSnapshotFromRemote({
      hasSession: () => !!useAuthStore.getState().session,
      force: Boolean(opts?.forceCatalog || opts?.force),
    });
    markMastersSyncedOk(now);
  }

  if (opts?.includeCustomer) {
    await pullCustomerDataWhenNeeded();
  }

  lastRemoteRefreshAt = Date.now();
}

/**
 * Bajada automática de catálogo (+ stock en snapshot).
 * Customer solo cuando `includeCustomer: true` (lazy).
 * Caja/ventas no se espejan (thin mirror — lectura online).
 */
export async function refreshRemoteDomains(opts?: RefreshRemoteDomainsOpts): Promise<void> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = runRefreshRemoteDomains(opts).finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/** Expuesto para tests / invalidación tras Realtime. */
export function resetRemoteRefreshCooldownForTests(): void {
  lastRemoteRefreshAt = 0;
  refreshInFlight = null;
}
